import type { ReactNode } from "react";
import { useRelease150 } from "../lib/release150";

// Loading placeholders. Never a number: a skeleton must not be mistaken for a figure. The pulse stops when motion is
// reduced (device setting or the person's "Reduce motion"; release 1.5.0 polish).

export function Skeleton({ className = "" }: { className?: string }) {
  return <div aria-hidden="true" className={`animate-pulse rounded-md bg-slate-200/70 motion-reduce:animate-none ${className}`} />;
}

/** Shown while a page's data is on its way (and while a lazily loaded page's code arrives). */
export function PageSkeleton() {
  return (
    <div className="space-y-4" role="status" aria-live="polite">
      <span className="sr-only">Loading…</span>
      <Skeleton className="h-7 w-64" />
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </div>
      <Skeleton className="h-64" />
    </div>
  );
}

const ROW_WIDTHS = ["w-full", "w-11/12", "w-4/5", "w-full", "w-3/4", "w-5/6"];

/**
 * A few grey lines where a list, a panel or a menu is loading (release 1.5.0 polish), announced once as "Loading…" by
 * its label. On a 1.4.0 screen pass `legacy` (what the screen showed before): with release 1.5.0 off that is shown
 * instead, so nothing changes until 1.5.0 ships.
 */
export function LoadingRows({
  rows = 3,
  label = "Loading…",
  legacy,
  className = "",
  rowClassName = "h-4"
}: {
  rows?: number;
  label?: string;
  legacy?: ReactNode;
  className?: string;
  rowClassName?: string;
}) {
  const release150 = useRelease150();
  if (!release150 && legacy !== undefined) return <>{legacy}</>;
  return (
    <div className={`space-y-2 ${className}`} role="status" aria-live="polite" data-testid="loading-rows">
      <span className="sr-only">{label}</span>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className={`${rowClassName} ${ROW_WIDTHS[i % ROW_WIDTHS.length]}`} />
      ))}
    </div>
  );
}
